import { create } from 'zustand';

export interface ClientInfo {
  clientId: string;
  virtualIP: string | null;
  authenticated: boolean;
  connectedAt: number;
}

export interface MonitoredSession {
  identifier: string;
  hostConnected: boolean;
  hostConnectedAt: number | null;
  clients: ClientInfo[];
  packetStats: {
    hostToClient: number;
    clientToHost: number;
  };
  lastActivity: number;
}

export interface PacketEvent {
  sessionId: string;
  direction: 'host_to_client' | 'client_to_host';
  targetType: 'broadcast' | 'unicast';
  payloadSize: number;
  timestamp: number;
  srcIP?: string | null;
  srcPort?: number | null;
  dstIP?: string | null;
  dstPort?: number | null;
  payloadPreview?: string | null; // base64-encoded first 48 bytes
}

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';

interface MonitorState {
  // Auth state
  token: string | null;
  email: string | null;
  isAuthenticated: boolean;

  // Connection state
  connectionStatus: ConnectionStatus;

  // Session data
  sessions: Map<string, MonitoredSession>;
  selectedSessionId: string | null;
  recentPackets: PacketEvent[];

  // Decryption keys (session ID -> net_key hex)
  decryptionKeys: Map<string, string>;

  // Actions
  setAuth: (token: string, email: string) => void;
  clearAuth: () => void;
  setConnectionStatus: (status: ConnectionStatus) => void;
  setSnapshot: (sessions: MonitoredSession[]) => void;
  updateSession: (session: MonitoredSession) => void;
  removeSession: (sessionId: string) => void;
  addPacket: (event: PacketEvent) => void;
  selectSession: (sessionId: string | null) => void;
  setDecryptionKey: (sessionId: string, key: string) => void;
  clearDecryptionKey: (sessionId: string) => void;
}

const MAX_RECENT_PACKETS = 100;

export const useMonitorStore = create<MonitorState>((set) => ({
  // Initial state
  token: localStorage.getItem('monitor_token'),
  email: localStorage.getItem('monitor_email'),
  isAuthenticated: !!localStorage.getItem('monitor_token'),

  connectionStatus: 'disconnected',

  sessions: new Map(),
  selectedSessionId: null,
  recentPackets: [],

  decryptionKeys: new Map(
    JSON.parse(localStorage.getItem('monitor_keys') || '[]')
  ),

  // Actions
  setAuth: (token, email) => {
    localStorage.setItem('monitor_token', token);
    localStorage.setItem('monitor_email', email);
    set({ token, email, isAuthenticated: true });
  },

  clearAuth: () => {
    localStorage.removeItem('monitor_token');
    localStorage.removeItem('monitor_email');
    set({ token: null, email: null, isAuthenticated: false });
  },

  setConnectionStatus: (status) => set({ connectionStatus: status }),

  setSnapshot: (sessions) => {
    const map = new Map<string, MonitoredSession>();
    for (const session of sessions) {
      map.set(session.identifier, session);
    }
    set({ sessions: map });
  },

  updateSession: (session) =>
    set((state) => {
      const sessions = new Map(state.sessions);
      sessions.set(session.identifier, session);
      return { sessions };
    }),

  removeSession: (sessionId) =>
    set((state) => {
      const sessions = new Map(state.sessions);
      sessions.delete(sessionId);
      // Clear selection if removed
      const selectedSessionId =
        state.selectedSessionId === sessionId ? null : state.selectedSessionId;
      return { sessions, selectedSessionId };
    }),

  addPacket: (event) =>
    set((state) => {
      const recentPackets = [event, ...state.recentPackets].slice(
        0,
        MAX_RECENT_PACKETS
      );

      // Update session packet stats locally for immediate counter updates
      const session = state.sessions.get(event.sessionId);
      if (session) {
        const sessions = new Map(state.sessions);
        const updatedSession = {
          ...session,
          packetStats: {
            hostToClient:
              session.packetStats.hostToClient +
              (event.direction === 'host_to_client' ? 1 : 0),
            clientToHost:
              session.packetStats.clientToHost +
              (event.direction === 'client_to_host' ? 1 : 0),
          },
          lastActivity: event.timestamp,
        };
        sessions.set(event.sessionId, updatedSession);
        return { recentPackets, sessions };
      }

      return { recentPackets };
    }),

  selectSession: (sessionId) => set({ selectedSessionId: sessionId }),

  setDecryptionKey: (sessionId, key) =>
    set((state) => {
      const decryptionKeys = new Map(state.decryptionKeys);
      decryptionKeys.set(sessionId, key);
      localStorage.setItem(
        'monitor_keys',
        JSON.stringify(Array.from(decryptionKeys.entries()))
      );
      return { decryptionKeys };
    }),

  clearDecryptionKey: (sessionId) =>
    set((state) => {
      const decryptionKeys = new Map(state.decryptionKeys);
      decryptionKeys.delete(sessionId);
      localStorage.setItem(
        'monitor_keys',
        JSON.stringify(Array.from(decryptionKeys.entries()))
      );
      return { decryptionKeys };
    }),
}));
